import React, { useCallback, useEffect, useState } from 'react';
import { Typography, Input, Select, Tag, Button, Space, Spin, Alert, Rate, Popconfirm } from 'antd';
import { message } from '../../../services/toastMessage';
import { IconStar } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import {
  getAdminReviews,
  hideAdminReview,
  unhideAdminReview,
  deleteAdminReview,
  IAdminReview,
} from '../../../services/AdminService';
import EmptyState from '../../../components/EmptyState';
import AdminReasonModal from '../../../components/Admin/AdminReasonModal';
import AdminResponsiveList from '../../../components/Admin/AdminResponsiveList';
import { TRANSLATIONS } from '../../../constants/translations';
import { translate } from '../../../helpers/translationHelper';
import { textSorter, numberSorter, booleanColumnFilters, booleanOnFilter } from '../../../helpers/adminTableHelpers';

const { Title, Text } = Typography;
const { Search } = Input;

type HiddenFilter = 'all' | 'hidden' | 'visible';

// Queue of reported/flagged reviews -- there's no formal report mechanism
// yet (see the parked "Reportes" section 8), so for now this lists every
// review with a hide (reversible)/delete (permanent) action, each requiring
// a recorded reason.
const ReviewsSection: React.FC = () => {
  const { t } = useTranslation();
  const [reviews, setReviews] = useState<IAdminReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [filter, setFilter] = useState<HiddenFilter>('all');
  const [search, setSearch] = useState<string | undefined>(undefined);

  const [hideTargetId, setHideTargetId] = useState<string | undefined>(undefined);
  const [deleteTargetId, setDeleteTargetId] = useState<string | undefined>(undefined);

  const loadReviews = useCallback(() => {
    setLoading(true);
    setError(false);
    const hidden = filter === 'all' ? undefined : filter === 'hidden';
    getAdminReviews(hidden, search)
      .then(setReviews)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [filter, search]);

  useEffect(() => {
    loadReviews();
  }, [loadReviews]);

  const handleHide = async (reason: string) => {
    if (!hideTargetId) return;
    try {
      await hideAdminReview(hideTargetId, reason);
      message.success(translate(t, TRANSLATIONS.ADMIN_ACTION_SUCCESS));
      setHideTargetId(undefined);
      loadReviews();
    } catch {
      message.error(translate(t, TRANSLATIONS.ADMIN_ACTION_ERROR));
    }
  };

  const handleDelete = async (reason: string) => {
    if (!deleteTargetId) return;
    try {
      await deleteAdminReview(deleteTargetId, reason);
      message.success(translate(t, TRANSLATIONS.ADMIN_ACTION_SUCCESS));
      setDeleteTargetId(undefined);
      loadReviews();
    } catch {
      message.error(translate(t, TRANSLATIONS.ADMIN_ACTION_ERROR));
    }
  };

  const handleUnhide = async (id: string) => {
    try {
      await unhideAdminReview(id);
      message.success(translate(t, TRANSLATIONS.ADMIN_ACTION_SUCCESS));
      loadReviews();
    } catch {
      message.error(translate(t, TRANSLATIONS.ADMIN_ACTION_ERROR));
    }
  };

  // Shared by the desktop "Acciones" column and the mobile card footer.
  const renderActions = (record: IAdminReview) => (
    <Space wrap>
      {record.isHidden ? (
        <Popconfirm title={translate(t, TRANSLATIONS.ADMIN_REVIEWS_UNHIDE_CONFIRM)} onConfirm={() => handleUnhide(record.id)}>
          <Button size="small">{translate(t, TRANSLATIONS.ADMIN_REVIEWS_UNHIDE_BUTTON)}</Button>
        </Popconfirm>
      ) : (
        <Button size="small" onClick={() => setHideTargetId(record.id)}>
          {translate(t, TRANSLATIONS.ADMIN_REVIEWS_HIDE_BUTTON)}
        </Button>
      )}
      <Button size="small" danger onClick={() => setDeleteTargetId(record.id)}>
        {translate(t, TRANSLATIONS.ADMIN_REVIEWS_DELETE_BUTTON)}
      </Button>
    </Space>
  );

  return (
    <div>
      <Title level={4} style={{ marginBottom: 20 }}>
        {translate(t, TRANSLATIONS.ADMIN_NAV_REVIEWS)}
      </Title>

      <Space style={{ marginBottom: 16 }} wrap>
        <Search
          allowClear
          placeholder={translate(t, TRANSLATIONS.ADMIN_REVIEWS_SEARCH_PLACEHOLDER)}
          style={{ maxWidth: 280, width: '100%' }}
          onSearch={(value) => setSearch(value || undefined)}
        />
        <Select
          style={{ maxWidth: 200, width: '100%' }}
          value={filter}
          onChange={setFilter}
          options={[
            { value: 'all', label: translate(t, TRANSLATIONS.ADMIN_REVIEWS_FILTER_ALL) },
            { value: 'hidden', label: translate(t, TRANSLATIONS.ADMIN_REVIEWS_FILTER_HIDDEN) },
            { value: 'visible', label: translate(t, TRANSLATIONS.ADMIN_REVIEWS_FILTER_VISIBLE) },
          ]}
        />
      </Space>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 32 }}>
          <Spin />
        </div>
      ) : error ? (
        <Alert type="error" showIcon message={translate(t, TRANSLATIONS.ADMIN_REVIEWS_LOAD_ERROR)} />
      ) : reviews.length === 0 ? (
        <EmptyState
          icon={<IconStar size={22} />}
          title={translate(t, TRANSLATIONS.ADMIN_REVIEWS_EMPTY_TITLE)}
          description={translate(t, TRANSLATIONS.ADMIN_REVIEWS_EMPTY_DESCRIPTION)}
        />
      ) : (
        <AdminResponsiveList<IAdminReview>
          rowKey="id"
          dataSource={reviews}
          pagination={{ pageSize: 20 }}
          scroll={{ x: 'max-content' }}
          toCard={(record) => ({
            key: record.id,
            title: record.heavyDutyMachineryTitle,
            subtitle: record.guestName,
            badge: (
              <Tag color={record.isHidden ? 'red' : 'green'} style={{ marginInlineEnd: 0 }}>
                {translate(t, record.isHidden ? TRANSLATIONS.ADMIN_REVIEWS_STATUS_HIDDEN : TRANSLATIONS.ADMIN_REVIEWS_STATUS_VISIBLE)}
              </Tag>
            ),
            fields: [
              {
                label: translate(t, TRANSLATIONS.ADMIN_REVIEWS_COL_RATING),
                value: <Rate disabled value={record.rating} style={{ fontSize: 14 }} />,
                wide: true,
              },
              { label: translate(t, TRANSLATIONS.ADMIN_REVIEWS_COL_COMMENT), value: record.comment ?? '—', wide: true },
              {
                label: translate(t, TRANSLATIONS.ADMIN_REVIEWS_COL_STATUS),
                value: record.isHidden ? record.hiddenReason : undefined,
                wide: true,
              },
            ],
            actions: renderActions(record),
          })}
          columns={[
            {
              title: translate(t, TRANSLATIONS.ADMIN_REVIEWS_COL_SPACE),
              dataIndex: 'heavyDutyMachineryTitle',
              key: 'heavyDutyMachineryTitle',
              sorter: textSorter<IAdminReview>('heavyDutyMachineryTitle'),
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_REVIEWS_COL_GUEST),
              dataIndex: 'guestName',
              key: 'guestName',
              sorter: textSorter<IAdminReview>('guestName'),
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_REVIEWS_COL_RATING),
              dataIndex: 'rating',
              key: 'rating',
              sorter: numberSorter<IAdminReview>('rating'),
              render: (value: number) => <Rate disabled value={value} style={{ fontSize: 14 }} />,
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_REVIEWS_COL_COMMENT),
              dataIndex: 'comment',
              key: 'comment',
              ellipsis: true,
              render: (value?: string) => value ?? '—',
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_REVIEWS_COL_STATUS),
              dataIndex: 'isHidden',
              key: 'isHidden',
              filters: booleanColumnFilters(
                translate(t, TRANSLATIONS.ADMIN_REVIEWS_STATUS_HIDDEN),
                translate(t, TRANSLATIONS.ADMIN_REVIEWS_STATUS_VISIBLE)
              ),
              onFilter: booleanOnFilter<IAdminReview>('isHidden'),
              render: (value: boolean, record: IAdminReview) => (
                <Space direction="vertical" size={0}>
                  <Tag color={value ? 'red' : 'green'}>
                    {translate(t, value ? TRANSLATIONS.ADMIN_REVIEWS_STATUS_HIDDEN : TRANSLATIONS.ADMIN_REVIEWS_STATUS_VISIBLE)}
                  </Tag>
                  {value && record.hiddenReason && <Text type="secondary" style={{ fontSize: 12 }}>{record.hiddenReason}</Text>}
                </Space>
              ),
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_REVIEWS_COL_ACTIONS),
              key: 'actions',
              render: (_: unknown, record: IAdminReview) => renderActions(record),
            },
          ]}
        />
      )}

      <AdminReasonModal
        open={hideTargetId !== undefined}
        title={translate(t, TRANSLATIONS.ADMIN_REVIEWS_HIDE_MODAL_TITLE)}
        confirmLabel={translate(t, TRANSLATIONS.ADMIN_REVIEWS_HIDE_BUTTON)}
        onCancel={() => setHideTargetId(undefined)}
        onConfirm={handleHide}
      />

      <AdminReasonModal
        open={deleteTargetId !== undefined}
        title={translate(t, TRANSLATIONS.ADMIN_REVIEWS_DELETE_MODAL_TITLE)}
        confirmLabel={translate(t, TRANSLATIONS.ADMIN_REVIEWS_DELETE_BUTTON)}
        onCancel={() => setDeleteTargetId(undefined)}
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default ReviewsSection;
